import React from 'react';
import { Mail, MessageSquare, Globe, StickyNote, Activity, FileText, Image as ImageIcon, Paperclip, Star } from 'lucide-react';
import { fmtDateTime } from '@/lib/utils';

// One entry on the case timeline. Four distinct treatments so the shape of a
// conversation is readable at a glance: a dark card for the customer, a tinted
// card for our own sends, a yellow card for internal notes, and a grey pill for
// system events.

function AttachmentChips({ names = [], urls = [] }) {
  if (!names.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {names.map((name, i) => {
        const url = urls[i];
        const ext = String(name).split('.').pop().toLowerCase();
        const Icon = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) ? ImageIcon : FileText;
        const inner = (
          <>
            <Icon className="w-3.5 h-3.5 text-primary shrink-0" />
            <span className="truncate">{name}</span>
          </>
        );
        // Only a document we generated and sent has a stored file behind it, so
        // only those are clickable; a customer's inbound file is a name only.
        return url ? (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/5 hover:bg-primary/10 text-white/70 text-[11px] px-2.5 py-1.5 max-w-[240px] transition-colors"
          >
            {inner}
          </a>
        ) : (
          <span
            key={i}
            className="inline-flex items-center gap-1.5 border border-white/10 bg-white/[0.02] text-white/45 text-[11px] px-2.5 py-1.5 max-w-[240px]"
          >
            {inner}
          </span>
        );
      })}
    </div>
  );
}

const CHANNEL_LABEL = { email: 'EMAIL', sms: 'SMS', webform: 'WEB FORM' };

export default function CaseActivityItem({ activity }) {
  const {
    channel,
    direction,
    subject,
    body,
    actor_name,
    actor_email,
    occurred_at,
    delivery_status,
    attachments,
    attachment_urls,
  } = activity;

  // ── System event — compact grey audit pill ───────────────────────────────
  if (channel === 'system_event') {
    return (
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 border border-white/5 bg-white/[0.03] px-3 py-1.5 max-w-full">
          <Activity className="w-3 h-3 text-white/30 shrink-0" />
          <p className="text-white/40 text-[11px] truncate">{subject}</p>
          <span className="text-white/20 text-[10px] shrink-0">{fmtDateTime(occurred_at)}</span>
        </div>
      </div>
    );
  }

  // ── Customer feedback — a rated card, distinct from an ordinary reply ────
  if (channel === 'customer_feedback') {
    const match = String(subject || '').match(/(\d)\s*\/\s*5/);
    const score = match ? Number(match[1]) : 0;
    return (
      <div className="border border-cyan-400/30 bg-cyan-400/[0.05]">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-cyan-400/20">
          <div className="flex items-center gap-2 min-w-0">
            <Star className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
            <span className="text-cyan-300 text-[10px] font-bold tracking-widest shrink-0">
              CUSTOMER FEEDBACK
            </span>
            {score > 0 && (
              <span className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-3 h-3 ${n <= score ? 'text-cyan-300 fill-cyan-300' : 'text-white/15'}`}
                  />
                ))}
              </span>
            )}
          </div>
          <span className="text-white/30 text-[10px] shrink-0">{fmtDateTime(occurred_at)}</span>
        </div>
        <div className="px-4 py-3">
          {score === 0 && subject && <p className="text-white text-xs font-bold mb-2">{subject}</p>}
          <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">
            {body || 'No comments left.'}
          </p>
          <p className="text-white/30 text-[10px] mt-2">{actor_name || actor_email}</p>
        </div>
      </div>
    );
  }

  // ── Internal note — yellow, unmistakably staff-only ──────────────────────
  if (channel === 'staff_note' || direction === 'internal') {
    return (
      <div className="border border-amber-400/40 bg-amber-400/[0.06]">
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-amber-400/20">
          <div className="flex items-center gap-2 min-w-0">
            <StickyNote className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="text-amber-400 text-[10px] font-bold tracking-widest truncate">
              INTERNAL NOTE — NOT VISIBLE TO CUSTOMER
            </span>
          </div>
          <span className="text-amber-400/60 text-[10px] shrink-0">{fmtDateTime(occurred_at)}</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-amber-50/80 text-sm whitespace-pre-wrap leading-relaxed">{body}</p>
          <p className="text-amber-400/50 text-[10px] mt-2">{actor_name || actor_email}</p>
        </div>
      </div>
    );
  }

  const inbound = direction !== 'outbound';
  const ChannelIcon = channel === 'sms' ? MessageSquare : channel === 'webform' ? Globe : Mail;

  // ── Customer inbound — clean dark card ───────────────────────────────────
  if (inbound) {
    return (
      <div className="border border-white/10 bg-[#111115]">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5">
          <div className="flex items-center gap-2 min-w-0">
            <ChannelIcon className="w-3.5 h-3.5 text-primary shrink-0" />
            <span className="text-white/70 text-[10px] font-bold tracking-widest shrink-0">
              {CHANNEL_LABEL[channel] || 'MESSAGE'}
            </span>
            <span className="text-white/30 text-[10px] truncate">
              {actor_name || actor_email}
            </span>
          </div>
          <span className="text-white/30 text-[10px] shrink-0">{fmtDateTime(occurred_at)}</span>
        </div>
        <div className="px-4 py-3">
          {subject && subject !== 'Internal note' && (
            <p className="text-white text-xs font-bold mb-2">{subject}</p>
          )}
          <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">{body}</p>
          <AttachmentChips names={attachments} urls={attachment_urls} />
        </div>
      </div>
    );
  }

  // ── Staff outbound — tinted, with sender and delivery state ──────────────
  return (
    <div className="border border-primary/25 bg-primary/[0.05]">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-primary/15">
        <div className="flex items-center gap-2 min-w-0">
          <ChannelIcon className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-primary text-[10px] font-bold tracking-widest shrink-0">
            SENT VIA {CHANNEL_LABEL[channel] || 'MESSAGE'}
          </span>
          <span className="text-white/40 text-[10px] truncate">{actor_name || actor_email}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {delivery_status && (
            <span className="text-green-400/70 text-[10px] font-bold tracking-wider uppercase">
              {delivery_status}
            </span>
          )}
          <span className="text-white/30 text-[10px]">{fmtDateTime(occurred_at)}</span>
        </div>
      </div>
      <div className="px-4 py-3">
        {subject && <p className="text-white text-xs font-bold mb-2">{subject}</p>}
        <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">{body}</p>
        <AttachmentChips names={attachments} urls={attachment_urls} />
        {attachments?.length > 0 && (
          <p className="text-white/25 text-[10px] mt-2 flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> {attachments.length} attachment
            {attachments.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}